import { useId, type ReactNode } from 'react';
import { ChartReadout, ChartTip, useActiveMark, type TipContent } from './ChartTip';
import { frameOf, labelEvery, midOf, yOf, type Frame } from './columns-geometry';
import {
  Bar,
  Crosshair,
  GhostLine,
  Grid,
  Projection,
  Hatch,
  Line,
  Reference,
  SpanBand,
  SpanHandles,
} from './ColumnsParts';
import { ordered, useColumnsPointer, type ColumnSpan } from './useColumnsPointer';
import { useMeasuredWidth } from './useMeasuredWidth';
import { useReport } from './useReport';
import './charts.css';

export interface Column {
  /** The axis label; the chart shows as many as fit, counted back from the last. */
  axis: string;
  value: number;
  /** A bucket still filling (this month so far): hatched, so it never reads as a drop. */
  partial?: boolean | undefined;
  /**
   * Where a bucket still filling is projected to end (the pace to the refill): a hollow outline from
   * the column's top up to it, so the chart and the pace say the same thing (round 3, dataviz M1).
   */
  projected?: number | undefined;
  /** A label drawn above the column (the peak, the latest); keep to two or three a chart. */
  mark?: string | undefined;
  tip: TipContent;
}

export type { ColumnSpan } from './useColumnsPointer';

export interface ColumnsProps {
  columns: readonly Column[];
  /** Spoken summary of the whole picture. */
  label: string;
  /** A line on the same axis and unit, one value a column (a 7-day average). */
  line?: readonly (number | undefined)[] | undefined;
  /** The previous period, one value a column, drawn as a quiet line; undefined where there is none. */
  ghost?: readonly (number | undefined)[] | undefined;
  /** A horizontal reference in the same unit: the plan's credits a month. */
  reference?: { value: number; label: string } | undefined;
  format?: (n: number) => string;
  height?: number;
  /** The columns whose detail is open; the rest dim. */
  selected?: ColumnSpan | null | undefined;
  /** A click, Enter or Space on one column. */
  onSelect?: ((index: number) => void) | undefined;
  /** A drag across columns, or Shift with an arrow: a span. */
  onSpan?: ((from: number, to: number) => void) | undefined;
  /** Linked emphasis from another chart: the columns it returns true for dim. */
  dim?: ((index: number) => boolean) | undefined;
  /** The column under the pointer or the keyboard, for the charts linked to this one. */
  onActive?: ((index: number | null) => void) | undefined;
  /** Whole things (companies): the axis ticks whole numbers only. */
  counts?: boolean | undefined;
  /** Label every this many columns instead of what fits (a histogram labels each bin). */
  axisEvery?: number | undefined;
}

function Axis({ columns, f }: { columns: readonly Column[]; f: Frame }): ReactNode {
  const every = labelEvery(f, columns.length);
  const last = columns.length - 1;
  return (
    <>
      {columns.map((c, i) => {
        if ((last - i) % every !== 0 || !c.axis) return null;
        const x = midOf(f, i);
        const anchor = x > f.width - 30 ? 'end' : x < f.left + 20 ? 'start' : 'middle';
        return (
          <text key={i} x={x} y={f.height - 6} textAnchor={anchor}>
            {c.axis}
          </text>
        );
      })}
      {columns.map((c, i) =>
        c.mark ? (
          <text
            key={`m${i}`}
            className="pui-chart__mark"
            x={midOf(f, i)}
            y={yOf(f, Math.max(c.value, c.projected ?? 0)) - 6}
            textAnchor="middle"
          >
            {c.mark}
          </text>
        ) : null,
      )}
    </>
  );
}

function stateOf(i: number, active: number | null, props: ColumnsProps, span: ColumnSpan | null) {
  const inSpan = span !== null && i >= span.from && i <= span.to;
  const linked = span === null && (props.dim?.(i) ?? false);
  return { on: i === active, picked: inSpan, dim: (span !== null && !inSpan) || linked };
}

/** Every value the axis must reach: the columns, the line, the ghost and the reference. */
function valuesOf(props: ColumnsProps): number[] {
  const extra = [props.line ?? [], props.ghost ?? []].flat();
  return [
    ...props.columns.map((c) => Math.max(c.value, c.projected ?? 0)),
    ...extra.filter((v): v is number => v !== undefined),
    props.reference?.value ?? 0,
  ];
}

function spokenLabel(props: ColumnsProps): string {
  return props.onSelect
    ? `${props.label} Press Enter on a column for its detail; Shift and an arrow choose a span.`
    : props.label;
}

interface Drawn {
  f: Frame;
  hatch: string;
  active: number | null;
  span: ColumnSpan | null;
  /** True after a pick, until the pointer moves: no crosshair over what was just chosen. */
  quiet: boolean;
}

/** Everything inside the SVG, back to front. */
function Marks({ props, d }: { props: ColumnsProps; d: Drawn }): ReactNode {
  const { f, hatch, active, span } = d;
  return (
    <>
      <Hatch id={hatch} />
      <Grid f={f} format={props.format ?? String} />
      {span ? <SpanBand f={f} from={span.from} to={span.to} /> : null}
      {props.columns.map((c, i) =>
        c.projected !== undefined ? (
          <Projection key={`p${i}`} from={c.value} to={c.projected} i={i} f={f} />
        ) : null,
      )}
      {props.columns.map((c, i) => (
        <Bar
          key={i}
          value={c.value}
          partial={c.partial ?? false}
          i={i}
          f={f}
          hatch={hatch}
          state={stateOf(i, active, props, span)}
        />
      ))}
      {props.ghost ? <GhostLine values={props.ghost} f={f} /> : null}
      {props.line ? <Line values={props.line} f={f} /> : null}
      {props.reference ? (
        <Reference value={props.reference.value} label={props.reference.label} f={f} />
      ) : null}
      {active !== null && !d.quiet ? <Crosshair f={f} i={active} /> : null}
      {span && props.onSpan ? <SpanHandles f={f} from={span.from} to={span.to} /> : null}
      <Axis columns={props.columns} f={f} />
    </>
  );
}

/**
 * The tooltip at the active column on a desk, or the readout strip above the chart on a phone,
 * which shows the latest column until a finger moves along the chart (round 2, dataviz S4).
 */
function Tip({
  columns,
  f,
  active,
  place,
}: {
  columns: readonly Column[];
  f: Frame;
  active: number | null;
  place: 'above' | 'over';
}) {
  const column = active === null ? undefined : columns[active];
  if (place === 'above') {
    return f.narrow ? (
      <ChartReadout
        tip={(column ?? columns.at(-1))?.tip ?? null}
        empty="Touch and slide along the chart to read it."
      />
    ) : null;
  }
  if (f.narrow || !column || active === null) return null;
  return (
    <ChartTip
      tip={column.tip}
      x={midOf(f, active)}
      y={Math.max(yOf(f, column.value) - 8, 96)}
      width={f.width}
    />
  );
}

/**
 * Columns on a zero baseline, drawn at the frame's measured width: an optional line and ghost line
 * on the same axis, a hatched partial bucket, a reference line, a few direct labels. Hover or the
 * arrow keys show a column; a click, Enter or a drag opens the detail behind one column or a span,
 * whose handles then stretch it. On a phone the tooltip becomes a readout strip above the chart.
 * Nothing for no columns.
 */
export function Columns(props: ColumnsProps) {
  const { columns, height = 220, onActive } = props;
  const hatch = `pui-hatch-${useId().replace(/:/g, '')}`;
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const mark = useActiveMark(columns.length, { onPick: props.onSelect, onSpan: props.onSpan });
  const f = frameOf(width, height, columns.length, valuesOf(props), {
    counts: props.counts,
    axisEvery: props.axisEvery,
  });
  const pointer = useColumnsPointer(f, columns.length, props, mark.setActive);
  useReport(mark.active, onActive);
  if (columns.length === 0) return null;
  const span = pointer.drag ? ordered(pointer.drag) : (props.selected ?? null);
  const pickable = props.onSelect !== undefined || props.onSpan !== undefined;
  return (
    <div className="pui-chart-frame" ref={ref}>
      <Tip columns={columns} f={f} active={mark.active} place="above" />
      <svg
        className={`pui-chart pui-chart--focusable${pickable ? ' pui-chart--pickable' : ''}`}
        width={f.width}
        height={height}
        viewBox={`0 0 ${f.width} ${height}`}
        role="img"
        aria-label={spokenLabel(props)}
        {...mark.focusProps}
        onKeyDown={(e) => {
          pointer.wake();
          mark.focusProps.onKeyDown(e);
        }}
        {...pointer.handlers}
      >
        <Marks props={props} d={{ f, hatch, active: mark.active, span, quiet: pointer.quiet }} />
      </svg>
      <Tip columns={columns} f={f} active={pointer.quiet ? null : mark.active} place="over" />
    </div>
  );
}
